import { 
    Cartesian3, 
    GeometryInstance, 
    PolygonGeometry, 
    PolygonHierarchy, 
    Primitive, 
    PrimitiveCollection, 
    Color, 
    PerInstanceColorAppearance,
    Resource,
    ColorGeometryInstanceAttribute,
    PolylineColorAppearance,
    PolylineGeometry
} from "cesium"

import { changeColor, createUUID } from '@/utils/custom-utils'
export default class PolygonLayer {

    /**
     * 
     * @param {string | number} id 
     * @param {Object} options 
     * @param {Array<Number> | String} [options.fillColor] 填充色
     * @param {Boolean} [options.showOutline] 是否显示外边框
     * @param {Boolean} [options.showFill] 是否显示面
     * @param {Array<Number> | String} [options.outlineColor] 边框色
     * @param {Number} [options.outlineWidth] 外边框宽度
     * @param {Array<Number>} [options.distanceDisplay]
     * @param {Boolean} [options.hasClick] 是否可点击
     * @param {Boolean} [options.hasHeight] 是否可点击
     */
    constructor(id,options) {
        this.id = id
        this.options = Object.assign({
            hasClick: true,
            showOutline: true,
            alpha: 0.4,
            showFill: true,
            outlineWidth: 1,
            outlineColor: '#FFFFFF',
            fillColor: '#FFFFFF'
        }, options)

        this.colors = {
            red: Color.RED,
            chartreuse: Color.CHARTREUSE,
            blue: Color.BLUE,
            yellow: Color.YELLOW,
            orange: Color.ORANGE,
            deeppink: Color.DEEPPINK,
            white: Color.WHITE,
            black: Color.BLACK,
            deepskyblue: Color.DEEPSKYBLUE,
            orangered: Color.ORANGERED
        }

        this.appearance = new PerInstanceColorAppearance({
            translucent: true,
            closed: false
        })
        this.outlineAppearance = new PolylineColorAppearance({
            translucent: true
        })
        this.instances = {}
        this.outlineInstances = {}
    }

    createPrimitive() {
        const polygonPrimitive = new Primitive({
            geometryInstances: Object.values(this.instances),
            appearance: this.appearance ,
            allowPicking: this.options.hasClick,
            compressVertices: false
        })

        const polylinePrimitive = new Primitive({
            geometryInstances: Object.values(this.outlineInstances),
            appearance: this.outlineAppearance ,
            allowPicking: false,
            compressVertices: true
        })
        
        return {polygonPrimitive, polylinePrimitive}
    }

    initInstance(polygons, hasHeight = false) {
        let instances = []
        for(let i=0; i<polygons.length; i++) {
            let polygonPoints = polygons[i].points
            let polygonId = polygons[i].id
            const instance = this.createInstance(polygonId, polygonPoints, hasHeight)
            instances.push(instance)
        }
        return instances
    }

    addList(polygons) {
        const _instances = this.initInstance(polygons)
        for(let i=0;i<_instances.length; i++) {
            let {polygonInstance, polylineInstance} = _instances[i]
            this.instances[polygonInstance.id] = polygonInstance
            this.outlineInstances[polylineInstance.id] = polylineInstance
        }
        this._resetLayer()
    }

    /**
     * 
     * @param {Array<Array<Number>>} points 
     */
    add(id, points, hasHeight = false) {
        const {polygonInstance, polylineInstance} = this.createInstance(id, points, hasHeight)
        this.instances[polygonInstance.id] = polygonInstance
        this.outlineInstances[polylineInstance.id] = polylineInstance
        this._resetLayer()
    }

    createInstance(id, points, hasHeight = false) {
        let _points = []
        for(let i=0; i<points.length; i++) {
            _points.push(...points[i])
        }
        let position = null
        if(hasHeight) {
            position = Cartesian3.fromDegreesArrayHeights(_points)
        }else {
            position = Cartesian3.fromDegreesArray(_points)
        }
        const polygon = new PolygonGeometry({
            polygonHierarchy: new PolygonHierarchy(position),
            vertexFormat: PerInstanceColorAppearance.DEFAULT
        })

        const polygonInstance = new GeometryInstance({
            geometry: polygon,
            attributes: {
                color: ColorGeometryInstanceAttribute.fromColor(changeColor(this.options.fillColor, this.options.alpha))
            },
            id: id
        })

        const polyline = new PolylineGeometry({
            positions: position,
            width: this.options.outlineWidth,
            vertexFormat: PolylineColorAppearance.VERTEX_FORMAT
        })

        const polylineInstance = new GeometryInstance({
            geometry: polyline,
            attributes: {
                color: ColorGeometryInstanceAttribute.fromColor(changeColor(this.options.outlineColor))
            },
            id: id
        })

        return {polygonInstance, polylineInstance}
    }

    /**
     * 
     * @param {String | Object} data 
     */
    addGeoJSON(data) {
        if(typeof data === 'string') {
            Resource.fetchJson({
                url: data
            }).then(json => {
                this.addInstancesByGeoJSON(json)
            })
        }else{
            this.addInstancesByGeoJSON(data)
        }
    }

    addInstancesByGeoJSON(json) {
        let _instances = []
        if(json.type === 'FeatureCollection') {
            let features = json.features
            for(let i=0; i<features.length; i++) {
                _instances.push(...this.createInstanceByFeature(features[i]))
            }
        }else if(json.type === 'Feature') {
            _instances = this.createInstanceByFeature(json)
        }

        if(_instances.length > 0) {
            for(let m=0; m<_instances.length; m++) {
                let {polygonInstance, polylineInstance} = _instances[m]
                this.instances[polygonInstance.id] = polygonInstance
                this.outlineInstances[polylineInstance.id] = polylineInstance
            }
            this._resetLayer()
        }
    }

    createInstanceByFeature(feature) {
        let id = feature.properties.id || createUUID()
        const geometry = feature.geometry
        if(!geometry) return []
        const coordinates = geometry.coordinates
        if(geometry.type === 'Polygon') {
            return this.createInstanceBySingle(id, coordinates)
        }else if(geometry.type === 'MultiPolygon') {
            return this.createInstanceByMulti(id, coordinates)
        }
    }

    createInstanceBySingle(id, polygon) {
        const ring = polygon[0]
        let positions = this.createPosition(ring)
        let holes = []
        for(let m=1; m<polygon.length; m++) {
            const hole = polygon[m]
            let holePosition = this.createPosition(hole)
            let holeHierarchy = new PolygonHierarchy(holePosition)
            holes.push(holeHierarchy)
        }
        const geometry = new PolygonGeometry({
            polygonHierarchy: new PolygonHierarchy(positions, holes),
            vertexFormat: PerInstanceColorAppearance.DEFAULT
        })

        const polygonInstance = new GeometryInstance({
            geometry: geometry,
            attributes: {
                color: ColorGeometryInstanceAttribute.fromColor(changeColor(this.options.fillColor, this.options.alpha))
            },
            id: id
        })

        const line = new PolylineGeometry({
            positions: positions,
            width: this.options.outlineWidth,
            vertexFormat: PolylineColorAppearance.VERTEX_FORMAT
        })

        const polylineInstance = new GeometryInstance({
            geometry: line,
            attributes: {
                color: ColorGeometryInstanceAttribute.fromColor(changeColor(this.options.outlineColor))
            },
            id: id
        })

        return [{polygonInstance, polylineInstance}]
    }

    createInstanceByMulti(id, coordinates) {
        let _instances = []
        for(let i=0;i< coordinates.length; i++) {
            const polygon = coordinates[i]
            const ring = polygon[0]
            let positions = this.createPosition(ring)
            let holes = []
            for(let m=1; m<polygon.length; m++) {
                const hole = polygon[m]
                let holePosition = this.createPosition(hole)
                let holeHierarchy = new PolygonHierarchy(holePosition)
                holes.push(holeHierarchy)
            }
            const geometry = new PolygonGeometry({
                polygonHierarchy: new PolygonHierarchy(positions, holes),
                vertexFormat: PerInstanceColorAppearance.DEFAULT
            })
    
            const polygonInstance = new GeometryInstance({
                geometry: geometry,
                attributes: {
                    color: ColorGeometryInstanceAttribute.fromColor(changeColor(this.options.fillColor, this.options.alpha))
                },
                id: id
            })

            const line = new PolylineGeometry({
                positions: positions,
                width: this.options.outlineWidth,
                vertexFormat: PolylineColorAppearance.VERTEX_FORMAT
            })
    
            const polylineInstance = new GeometryInstance({
                geometry: line,
                attributes: {
                    color: ColorGeometryInstanceAttribute.fromColor(changeColor(this.options.outlineColor))
                },
                id: id
            })

            _instances.push({polygonInstance, polylineInstance})
        }

        return _instances
    }

    createPosition(points) {
        let position = []
        for(let i=0;i<points.length; i++) {
            position.push(...points[i])
        }
        return Cartesian3.fromDegreesArray(position)
    }

    remove(id) {
        if(Object.keys(this.instances).includes(id)) {
            delete this.instances[id]
        }
        if(Object.keys(this.outlineInstances).includes(id)) {
            delete this.outlineInstances[id]
        }
        this._resetLayer()
    }

    changeStyleById(id, options) {
       let attribute = this.polygonPrimitive.getGeometryInstanceAttributes(id)
       attribute.color = ColorGeometryInstanceAttribute.fromColor(changeColor(options.fillColor))
    }

    clear() {
        this.instances = {}
        this.outlineInstances = {}
        this._resetLayer()
    }

    setData(geojson) {
        this.clear()
        this.addGeoJSON(geojson)
    }

    /**
     * 
     * @param {PrimitiveCollection} primitives 
     */
    addTo(primitives) {
        this.primitives = primitives.add(new PrimitiveCollection())
        let {polygonPrimitive, polylinePrimitive} = this.createPrimitive()
        if(this.options.showOutline) {
            this.polylinePrimitive = this.primitives.add(polylinePrimitive)
        }
        if(this.options.showFill) {
            this.polygonPrimitive = this.primitives.add(polygonPrimitive)
            this.primitives.lowerToBottom(this.polygonPrimitive)
        }
    }

    _resetLayer() {
        this.primitives.remove(this.polygonPrimitive)
        this.primitives.remove(this.polylinePrimitive)
        let {polygonPrimitive, polylinePrimitive} = this.createPrimitive()
        if(this.options.showOutline) {
            this.polylinePrimitive = this.primitives.add(polylinePrimitive)
        }
        if(this.options.showFill) {
            this.polygonPrimitive = this.primitives.add(polygonPrimitive)
            this.primitives.lowerToBottom(this.polygonPrimitive)
        }
        
    }

    setIsShowOutline(showOutline) {
        this.options.showOutline = showOutline
        this._resetLayer()
    }

    setIsShowFill(showFill) {
        this.options.showFill = showFill
        this._resetLayer()
    }

    destroy() {
        this.polygonPrimitive.destroy()
        this.polylinePrimitive.destroy()
    }

    getType() {
        return 'PolygonLayer'
    }
}